import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

(() => {
  if (customElements.get('bt-auto-cross-sell')) return;

  class BtAutoCrossSell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true') {
        this.initializeBundle();
        this.mainVariantInput?.addEventListener('change', this.handleMainVariantChange);
        this.productInfo?.addEventListener('shopify:product:select', this.handleProductSelect);
        this.existingUpsell?.classList.add('bt-auto-cross-sell-is-replaced');
        this.syncMainVariant();
        return;
      }

      if (this.dataset.loading === 'true') return;
      const url = this.dataset.url;
      if (!url) return;

      this.dataset.loading = 'true';

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const container = document.createElement('div');
          container.innerHTML = html;
          const recommendation = container.querySelector('bt-auto-cross-sell[data-loaded="true"]');

          if (!recommendation || !recommendation.innerHTML.trim()) {
            this.remove();
            return;
          }

          this.innerHTML = recommendation.innerHTML;
          this.dataset.loaded = 'true';
          this.dataset.mainProductId = recommendation.dataset.mainProductId || '';
          this.dataset.discountPercent = recommendation.dataset.discountPercent || '10';
          this.dataset.mainDelivery = recommendation.dataset.mainDelivery || 'standard';
          this.dataset.targetHeading = recommendation.dataset.targetHeading || '';
          this.dataset.maxVisible = recommendation.dataset.maxVisible || '3';
          this.removeAttribute('data-loading');
          this.removeAttribute('aria-busy');
          this.initializeBundle();
        })
        .catch(() => this.remove());
    }

    disconnectedCallback() {
      this.mainVariantInput?.removeEventListener('change', this.handleMainVariantChange);
      this.productInfo?.removeEventListener('shopify:product:select', this.handleProductSelect);
      this.existingUpsell?.classList.remove('bt-auto-cross-sell-is-replaced');
    }

    initializeBundle() {
      if (this.bundleInitialized) return;

      this.bundleInitialized = true;
      this.discountPercent = Number(this.dataset.discountPercent || 10);
      this.maxVisible = Number(this.dataset.maxVisible || 3);
      this.productInfo = this.closest('product-info, .product-information');
      this.existingUpsell = this.productInfo?.querySelector('#bogos-pa-upsell-view');
      this.existingUpsell?.classList.add('bt-auto-cross-sell-is-replaced');
      this.mainVariantInput = this.productInfo?.querySelector(
        'product-form input[name="id"], product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]'
      );
      this.mainVariants = this.parseMainVariants();
      this.bundleItems = [...this.querySelectorAll('[data-bt-bundle-item]')];
      this.currentDeliveryClass = null;
      this.visibleItemCount = 0;
      this.submitButton = this.querySelector('[data-bt-bundle-submit]');
      this.errorElement = this.querySelector('[data-bt-bundle-error]');

      this.handleMainVariantChange = () => this.syncMainVariant();
      this.handleProductSelect = (event) => {
        if (!event.promise) return;
        event.promise
          .then(({ detail }) => this.syncMainVariant(detail?.resource?.id))
          .catch((error) => {
            if (error?.name !== 'AbortError') console.warn('[bt-auto-cross-sell] Variant update failed:', error);
          });
      };
      this.mainVariantInput?.addEventListener('change', this.handleMainVariantChange);
      this.productInfo?.addEventListener('shopify:product:select', this.handleProductSelect);

      this.bundleItems.forEach((item) => {
        const checkbox = item.querySelector('[data-bt-bundle-checkbox]');
        const select = item.querySelector('[data-bt-variant-select]');

        checkbox?.addEventListener('change', () => {
          item.classList.toggle('is-selected', checkbox.checked);
          this.updateSummary();
        });

        select?.addEventListener('change', () => {
          this.updateItemVariant(item);
          this.updateSummary();
        });

        this.updateItemVariant(item);
      });

      this.submitButton?.addEventListener('click', () => this.addBundleToCart());
      this.syncMainVariant();
    }

    parseMainVariants() {
      const dataElement = this.querySelector('[data-bt-main-variants]');
      if (!dataElement) return [];

      try {
        return JSON.parse(dataElement.textContent);
      } catch (_error) {
        return [];
      }
    }

    syncMainVariant(explicitVariantId) {
      this.mainVariantInput = this.productInfo?.querySelector(
        'product-form input[name="id"], product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]'
      );
      const urlVariantId = new URL(window.location.href).searchParams.get('variant');
      const variantId = explicitVariantId?.toString() || urlVariantId || this.mainVariantInput?.value?.toString();
      const variant = this.mainVariants.find((candidate) => candidate.id.toString() === variantId)
        || this.mainVariants.find((candidate) => candidate.available)
        || this.mainVariants[0];

      this.currentMainVariant = variant;
      if (!variant) {
        this.currentDeliveryClass = this.dataset.mainDelivery || 'standard';
        this.filterBundleItemsForDelivery();
        this.updateSummary();
        return;
      }

      const previousDeliveryClass = this.currentDeliveryClass;
      this.currentDeliveryClass = variant.delivery || this.dataset.mainDelivery || 'standard';

      if (previousDeliveryClass && previousDeliveryClass !== this.currentDeliveryClass) {
        this.resetBundleSelection();
      }

      const title = this.querySelector('[data-bt-main-variant-title]');
      const price = this.querySelector('[data-bt-main-price]');
      const image = this.querySelector('.bt-auto-cross-sell__main-image');

      if (title) title.textContent = variant.title === 'Default Title' ? '選択済み' : variant.title;
      if (price) price.textContent = this.formatMoney(variant.price);
      if (image && variant.image) image.src = variant.image;

      this.filterBundleItemsForDelivery();
      this.updateSummary();
    }

    resetBundleSelection() {
      this.bundleItems.forEach((item) => {
        const checkbox = item.querySelector('[data-bt-bundle-checkbox]');
        if (checkbox) checkbox.checked = false;
        item.classList.remove('is-selected');
      });
    }

    filterBundleItemsForDelivery() {
      let visibleCount = 0;

      this.bundleItems.forEach((item) => {
        const checkbox = item.querySelector('[data-bt-bundle-checkbox]');
        const select = item.querySelector('[data-bt-variant-select]');
        const options = select ? [...select.options] : [];
        const matchingOptions = options.filter(
          (option) => option.dataset.available === 'true' && option.dataset.delivery === this.currentDeliveryClass
        );

        options.forEach((option) => {
          const isMatching = option.dataset.available === 'true' && option.dataset.delivery === this.currentDeliveryClass;
          option.hidden = !isMatching;
          option.disabled = !isMatching;
        });

        const shouldShow = matchingOptions.length > 0 && visibleCount < this.maxVisible;
        item.hidden = !shouldShow;

        if (!shouldShow) {
          if (checkbox) checkbox.checked = false;
          item.classList.remove('is-selected');
          return;
        }

        visibleCount += 1;
        const selectedOption = select?.selectedOptions?.[0];
        if (!selectedOption || !matchingOptions.includes(selectedOption)) {
          select.value = matchingOptions[0].value;
        }
        this.updateItemVariant(item);
      });

      this.visibleItemCount = visibleCount;
      const isInstant = this.currentDeliveryClass === 'instant';
      const status = this.querySelector('[data-bt-delivery-status]');
      const description = this.querySelector('[data-bt-delivery-description]');
      const targetHeading = this.dataset.targetHeading || '合わせて使える商品';

      if (status) status.textContent = isInstant ? '即納同士' : 'お取り寄せ同士';
      if (description) {
        const deliveryLabel = isInstant ? '即納対象' : 'お取り寄せ対象';
        const countCopy = visibleCount > 0 ? `最大${Math.min(this.maxVisible, visibleCount)}商品` : '該当商品';
        description.textContent = `${targetHeading}を、${deliveryLabel}から${countCopy}選べます。`;
      }
    }

    updateItemVariant(item) {
      const select = item.querySelector('[data-bt-variant-select]');
      const option = select?.selectedOptions?.[0];
      if (!option) return;

      const price = item.querySelector('[data-bt-item-price]');
      const image = item.querySelector('.bt-auto-cross-sell-card__image');

      if (price) price.textContent = this.formatMoney(Number(option.dataset.price || 0));
      if (image && option.dataset.image) image.src = option.dataset.image;
    }

    getSelectedItems() {
      return this.bundleItems
        .filter((item) => !item.hidden && item.querySelector('[data-bt-bundle-checkbox]')?.checked)
        .map((item) => {
          const select = item.querySelector('[data-bt-variant-select]');
          const option = select?.selectedOptions?.[0];
          return {
            productId: item.dataset.productId,
            variantId: select?.value,
            price: Number(option?.dataset.price || 0),
            delivery: option?.dataset.delivery,
          };
        })
        .filter((item) => item.variantId && item.delivery === this.currentDeliveryClass);
    }

    updateSummary() {
      const selectedItems = this.getSelectedItems();
      const mainPrice = Number(this.currentMainVariant?.price || 0);
      const subtotal = mainPrice + selectedItems.reduce((sum, item) => sum + item.price, 0);
      const eligible = Boolean(this.currentMainVariant?.available && selectedItems.length > 0);
      const saving = eligible ? Math.round(subtotal * (this.discountPercent / 100)) : 0;
      const setTotal = subtotal - saving;
      const itemCount = selectedItems.length + 1;

      const message = this.querySelector('[data-bt-summary-message]');
      const regularTotal = this.querySelector('[data-bt-regular-total]');
      const setTotalElement = this.querySelector('[data-bt-set-total]');
      const savingElement = this.querySelector('[data-bt-saving]');
      const submitLabel = this.querySelector('[data-bt-submit-label]');

      if (message) {
        message.textContent = eligible
          ? `${itemCount}点のセット価格`
          : this.visibleItemCount > 0
            ? '追加する商品を選択してください'
            : '同じ配送区分の商品がありません';
      }
      if (regularTotal) regularTotal.textContent = eligible ? this.formatMoney(subtotal) : '';
      if (setTotalElement) setTotalElement.textContent = eligible ? this.formatMoney(setTotal) : '';
      if (savingElement) savingElement.textContent = eligible ? `${this.formatMoney(saving)}お得` : '';
      if (submitLabel) {
        submitLabel.textContent = eligible
          ? `選択した${itemCount}点をまとめてカートへ`
          : '商品を1点以上選択してください';
      }
      if (this.submitButton) this.submitButton.disabled = !eligible;
    }

    async addBundleToCart() {
      const selectedItems = this.getSelectedItems();
      if (!this.currentMainVariant?.id || !this.currentMainVariant.available || selectedItems.length === 0) return;

      this.showError('');
      this.submitButton.classList.add('is-loading');
      this.submitButton.disabled = true;

      const setId = `bt-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const selectedProductIds = selectedItems.map((item) => item.productId).join(',');
      const sharedProperties = {
        _bt_set_id: setId,
        _bt_set_discount: this.discountPercent.toString(),
        _bt_set_delivery: this.currentDeliveryClass,
        _bt_set_source: window.location.pathname,
      };

      const items = [
        {
          id: this.currentMainVariant.id,
          quantity: 1,
          properties: {
            ...sharedProperties,
            _bt_set_role: 'main',
            _bt_set_products: selectedProductIds,
          },
        },
        ...selectedItems.map((item) => ({
          id: item.variantId,
          quantity: 1,
          properties: {
            ...sharedProperties,
            _bt_set_role: 'cross_sell',
            _bt_set_parent: this.dataset.mainProductId,
          },
        })),
      ];

      const cartItemsComponents = [
        ...document.querySelectorAll('cart-items-component[data-section-id]'),
      ];
      const cartSectionIds = cartItemsComponents
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);
      const requestBody = { items };

      if (cartSectionIds.length > 0) {
        requestBody.sections = cartSectionIds.join(',');
        requestBody.sections_url = window.location.pathname;
      }

      const deferredEventPromise = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({
            merchandiseId: item.id.toString(),
            quantity: item.quantity,
          })),
          promise: deferredEventPromise.promise,
        })
      );

      try {
        const rootPath = window.Shopify?.routes?.root || '/';
        const cartAddUrl = typeof routes !== 'undefined' ? routes.cart_add_url : `${rootPath}cart/add.js`;
        const cartUrl = typeof routes !== 'undefined' ? routes.cart_url : `${rootPath}cart`;
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(requestBody),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'カートへの追加に失敗しました。');
        }

        const cartResponse = await fetch(`${rootPath}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!cartResponse.ok) throw new Error('カート情報の更新に失敗しました。');
        const ajaxCart = await cartResponse.json();

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'bt-auto-cross-sell',
            sourceId: this.id || 'bt-auto-cross-sell',
            itemCount: items.length,
            productId: this.dataset.mainProductId,
            sections: data.sections,
            didError: false,
          },
        });

        if (cartItemsComponents.length === 0) {
          window.location.href = cartUrl;
        }
      } catch (error) {
        deferredEventPromise.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'カートへの追加に失敗しました。',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        this.showError(error.message || 'カートへの追加に失敗しました。時間をおいて再度お試しください。');
      } finally {
        this.submitButton.classList.remove('is-loading');
        this.updateSummary();
      }
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message;
      this.errorElement.hidden = !message;
    }

    formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || 'JPY';
      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency,
        maximumFractionDigits: currency === 'JPY' ? 0 : 2,
      }).format(Number(cents || 0) / 100);
    }
  }

  customElements.define('bt-auto-cross-sell', BtAutoCrossSell);
})();
